import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import {
  AgentSpamQueueItem,
  AgentSpamQueueFinalAction,
  AgentSpamQueueResolveAction,
  AgentSpamQueueResolveResult,
  AgentSpamSenderItem,
  AgentSpamSenderUnblockResult,
} from '../shared/agent-spam-queue';

/**
 * REST client for AG-10a's review queue, `/api/agent/spam-queue/*` on the
 * webapi backend -- `realm:portal-admin` only, same pattern as
 * `agent-dashboard-api.service.ts`. No `retry()` on the two POSTs (unlike
 * the GETs below), same reasoning as that service: a retried write against
 * an already-resolved row would just surface as a 409, but there's no
 * reason to risk it for a transient network blip when the caller can just
 * retry manually. Errors are not caught/alerted here -- callers subscribe
 * with their own `error` handler.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentSpamQueueApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  private _options(params?: Record<string, string>) {
    return {
      headers: this.globalsettingsservice.getStandardApiHeaders(),
      ...(params ? { params } : {}),
    };
  }

  getQueue(finalAction: AgentSpamQueueFinalAction = 'pending'): Observable<AgentSpamQueueItem[]> {
    return this.http.get<AgentSpamQueueItem[]>(this._getApiUrl('/api/agent/spam-queue'), this._options({ final_action: finalAction }))
      .pipe(retry(1));
  }

  resolveQueueItem(id: number, action: AgentSpamQueueResolveAction): Observable<AgentSpamQueueResolveResult> {
    return this.http.post<AgentSpamQueueResolveResult>(this._getApiUrl(`/api/agent/spam-queue/${id}/resolve`), { action }, this._options());
  }

  getSenders(autoBlockOnly: boolean = false): Observable<AgentSpamSenderItem[]> {
    const params = autoBlockOnly ? { auto_block: 'true' } : undefined;
    return this.http.get<AgentSpamSenderItem[]>(this._getApiUrl('/api/agent/spam-queue/senders'), this._options(params))
      .pipe(retry(1));
  }

  unblockSender(id: number): Observable<AgentSpamSenderUnblockResult> {
    return this.http.post<AgentSpamSenderUnblockResult>(this._getApiUrl(`/api/agent/spam-queue/senders/${id}/unblock`), {}, this._options());
  }
}
